/**
 * Setup module: the admin screens where a non-technical owner customises one business area.
 * Profile (identity, lists, timing, reminders), knowledge, the AI's extra instructions per engine,
 * selling-rule wording, version history + restore, export/import. Shapes and routes only; no logic.
 * Admin only (org admin). Registered by `export * from './mod-setup.js';` in index.ts.
 *
 * Prompt + output schema are ONE unit: owners edit the instruction TEXT only. Nothing here can change an
 * engine's output schema or validators.
 */
import { z } from 'zod';
import { Id, IsoDate, OverrideRequest, Timestamp } from './common.js';
import { PresignDownloadResponse, PresignUploadResponse } from './misc.js';
import { ProductPresignUploadRequest } from './mod-products-laws.js';

const Text = (max: number) => z.string().trim().max(max);
const Word = z.string().trim().min(1).max(80);
const List = (max = 40) => z.array(Word).max(max);
const HHMM = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'use HH:MM (24 hour, IST)');
const Url = z.union([z.literal(''), z.string().trim().max(300).regex(/^https?:\/\/\S+$/, 'must start with http:// or https://')]);

export const STAGE_CODES = ['A', 'P', 'B', 'C', 'D', 'E', 'F', 'O', 'G'] as const;
export const StageCode = z.enum(STAGE_CODES);

// ---------------------------------------------------------------- profile (groups 1, 5, 6, 7)
export const AreaIdentitySchema = z.object({
  businessName: Text(120),
  senderName: Text(60),
  brandNames: List(20),
  websiteUrl: Url,
  senderEmail: z.union([z.literal(''), z.string().trim().email().max(200)]),
  signature: Text(400),
  whatWeSell: Text(800),
  idealCustomer: Text(1200),
  expertPersona: Text(300),
  industryVocabulary: Text(1200),
  allowedAbbreviations: z.array(z.string().trim().regex(/^[A-Za-z]{2,10}$/, 'letters only, 2 to 10')).max(80),
  buyerRoles: List(20),
  qualificationGates: Text(2500),
  region: Text(200),
  currency: z.string().trim().regex(/^[A-Z]{3}$/, 'three capital letters, for example INR'),
  phoneCountryCode: z.string().regex(/^\d{1,3}$/, 'digits only, for example 91'),
  language: Text(120),
  toneNotes: Text(1200),
  linkedinAudience: z.enum(['b2b', 'pharma', 'founder']),
  linkedinIndustryLens: Text(800),
  bannedWordsExtra: List(60),
  bannedClaims: z.array(z.string().trim().min(3).max(200)).max(60),
}).strict();

export const CalendarEntrySchema = z.object({ date: IsoDate, name: z.string().trim().min(1).max(120), kind: z.enum(['festival', 'season', 'fiscal', 'exam']) }).strict();

export const AreaListsSchema = z.object({
  stageNames: z.object(Object.fromEntries(STAGE_CODES.map((c) => [c, z.string().trim().min(1).max(60)])) as Record<(typeof STAGE_CODES)[number], z.ZodString>).strict(),
  relationshipTypes: List(),
  blockers: List(),
  dealTypes: List(),
  jobTitles: List(),
  concerns: List(),
  leadSources: List(),
  channels: List(12),
  calendar: z.array(CalendarEntrySchema).max(200),
  /** Purpose choices on Draft > Write a message. Optional so older saved setups still load. */
  draftPurposes: List(20).optional(),
  /** What each contact type code means in the AI's facts (icp, champion ...). Optional so older saved setups still load. */
  contactTypeMeanings: z.record(z.string().regex(/^[a-z_]{2,20}$/, 'a contact type code, for example icp'), z.string().trim().min(1).max(120)).optional()
    .refine((m) => !m || Object.keys(m).length <= 30, { message: 'At most 30 contact types' }),
}).strict();

const Days = (min: number, max: number) => z.number().int().min(min).max(max);
export const AreaTimingSchema = z.object({
  staleDays: z.object(Object.fromEntries(STAGE_CODES.map((c) => [c, Days(0, 365)])) as Record<(typeof STAGE_CODES)[number], z.ZodNumber>).partial().strict(),
  twoTouchMax: Days(1, 5),
  absoluteTouchCeiling: Days(1, 10).nullable(),
  inboundAckWindowDays: Days(1, 90).nullable(),
  commitmentAnchorDays: Days(1, 60),
  commitmentAnchorSuggestDays: Days(1, 60),
  commitmentExpiryDays: Days(1, 90),
  commitmentExpiryPastNamedDays: Days(0, 30),
  payingOfferQuietDays: Days(1, 30),
  softCheckDays: Days(1, 30),
}).strict()
  .refine((t) => t.absoluteTouchCeiling === null || t.absoluteTouchCeiling >= t.twoTouchMax, { message: 'The most messages in all cannot be lower than the messages before resting', path: ['absoluteTouchCeiling'] })
  .refine((t) => t.commitmentAnchorSuggestDays <= t.commitmentAnchorDays, { message: 'The suggested check-back cannot be later than the limit', path: ['commitmentAnchorSuggestDays'] });

export const AreaRemindersSchema = z.object({
  dailyBriefOn: z.boolean(),
  dailyBriefTimeIst: HHMM,
  briefChannels: z.array(z.enum(['in_app', 'email', 'whatsapp'])).min(1).max(3),
  reminderLeadDays: Days(0, 14),
  festivalHeadsUpDays: Days(0, 30),
  quietHoursStartIst: HHMM,
  quietHoursEndIst: HHMM,
}).strict();

export const AreaProfileSchema = z.object({
  identity: AreaIdentitySchema,
  lists: AreaListsSchema,
  timing: AreaTimingSchema,
  reminders: AreaRemindersSchema,
}).strict();
export type AreaProfileT = z.infer<typeof AreaProfileSchema>;

export const SetupTemplate = z.enum(['pharma', 'enrollup', 'thinkwithai', 'blank']);
export type SetupTemplate = z.infer<typeof SetupTemplate>;

export const AreaSummary = z.object({
  verticalId: Id, key: z.string(), name: z.string(),
  profileVersion: z.number().int().nonnegative(), usesTemplate: z.boolean(), suggestedTemplate: SetupTemplate,
  knowledgeCount: z.number().int(), liveInstructions: z.number().int(), areaRules: z.number().int(), rewordedRules: z.number().int(),
  lastChangedAt: Timestamp.nullable(),
});
export const AreaProfileResponse = z.object({
  verticalId: Id, areaKey: z.string(), areaName: z.string(),
  profile: AreaProfileSchema,
  /** 0 = nothing saved yet: the starting template is shown. */
  version: z.number().int().nonnegative(),
  usesTemplate: z.boolean(),
  template: SetupTemplate,
  updatedAt: Timestamp.nullable(),
  updatedByName: z.string().nullable(),
});
export type AreaProfileResponse = z.infer<typeof AreaProfileResponse>;
export const PutAreaProfileRequest = z.object({ profile: AreaProfileSchema, version: z.number().int().nonnegative(), note: Text(300).optional() }).strict();
export const ApplyTemplateRequest = z.object({ template: SetupTemplate, version: z.number().int().nonnegative(), includeKnowledge: z.boolean().default(true) }).strict();
export const RenameAreaRequest = z.object({ name: z.string().trim().min(2).max(80) }).strict();

// ---------------------------------------------------------------- knowledge
export const KnowledgeKind = z.enum(['context', 'product_note', 'proof', 'faq_objection', 'competitor', 'document', 'banned_claim']);
export const KnowledgeItem = z.object({
  id: Id, verticalId: Id, kind: KnowledgeKind, title: z.string(), body: z.string(), sourceUrl: z.string().nullable(), fileId: Id.nullable(),
  active: z.boolean(), version: z.number().int().positive(), updatedAt: Timestamp, updatedByName: z.string().nullable(),
  /** Name of the attached document (private S3), when one is attached and uploaded. */
  fileName: z.string().nullable().optional(),
});
export type KnowledgeItem = z.infer<typeof KnowledgeItem>;
export const CreateKnowledgeRequest = z.object({
  kind: KnowledgeKind, title: z.string().trim().min(1).max(200), body: Text(20000).default(''),
  sourceUrl: Url.optional(), fileId: Id.nullable().optional(), active: z.boolean().default(true),
}).strict();
export const UpdateKnowledgeRequest = z.object({
  kind: KnowledgeKind.optional(), title: z.string().trim().min(1).max(200).optional(), body: Text(20000).optional(), sourceUrl: Url.optional(),
  /** Attach an uploaded document (null = detach it). */
  fileId: Id.nullable().optional(),
  active: z.boolean().optional(), version: z.number().int().positive(), note: Text(300).optional(),
}).strict();
/** Attach a document to knowledge: same shape and limits as product brochures (private S3, 300 s links). */
export const KnowledgePresignUploadRequest = ProductPresignUploadRequest;
export const KnowledgeFileOut = z.object({ fileId: Id, name: z.string(), status: z.enum(['pending', 'uploaded']) });
export const KnowledgeListQuery = z.object({ kind: KnowledgeKind.optional() });

// ---------------------------------------------------------------- AI instructions per engine
export const InstructionVersion = z.object({
  version: z.number().int().positive(), text: z.string(), status: z.enum(['draft', 'live', 'retired']), note: z.string().nullable(),
  basePromptVersion: z.string(), createdAt: Timestamp, createdByName: z.string().nullable(), publishedAt: Timestamp.nullable(), triedAt: Timestamp.nullable(),
});
export type InstructionVersion = z.infer<typeof InstructionVersion>;
export const EngineSummary = z.object({
  engineId: z.string(), label: z.string(), what: z.string(), group: z.string(),
  riskTier: z.enum(['low', 'medium', 'high']), basePromptVersion: z.string(), personBased: z.boolean(),
  live: InstructionVersion.nullable(), draft: InstructionVersion.nullable(),
  /** The live text was written for an older base prompt: worth a re-check. */
  liveIsForOlderBase: z.boolean(),
});
export const EngineDetail = EngineSummary.extend({
  /** Read-only: the instructions in code, with this area's details filled in (no owner text). */
  basePrompt: z.string(),
  /** What the AI also receives for this area (profile, knowledge, rules, the live extra instructions). */
  areaBlock: z.string(),
  history: z.array(InstructionVersion),
});
export type EngineDetail = z.infer<typeof EngineDetail>;
export const PutInstructionDraftRequest = z.object({ instructionsText: Text(4000), note: Text(300).optional() }).strict();
export const TryEngineRequest = z.object({
  /** Text to try; omitted = the saved draft (or the live text when there is no draft). */
  instructionsText: Text(4000).optional(),
  /** A real person to try it on (person-based engines only); omitted = the built-in sample. */
  relationshipId: Id.optional(),
}).strict();
export const TryEngineResponse = z.object({
  ai: z.enum(['ok', 'unavailable']),
  output: z.unknown().nullable(),
  errors: z.array(z.string()),
  usedSample: z.boolean(),
  personName: z.string(),
  /** true when this run tried the saved draft, which may now be published. */
  triedDraft: z.boolean(),
});
/** `override`: "Change anyway" after a 409 WARNING (e.g. making a draft live that was never tried). */
export const PublishInstructionRequest = z.object({ version: z.number().int().positive(), override: OverrideRequest.optional() }).strict();
export const RestoreInstructionRequest = z.object({ version: z.number().int().positive(), override: OverrideRequest.optional() }).strict();

/** People the owner can pick for "Try it" (this area only). */
export const TryPerson = z.object({ relationshipId: Id, name: z.string(), company: z.string(), stageName: z.string() });
export const SamplePeopleQuery = z.object({ q: z.string().trim().max(100).optional() });

// ---------------------------------------------------------------- timing preview (computed on the server)
export const TimingPreviewRequest = z.object({ timing: AreaTimingSchema }).strict();
const Pair = z.object({ current: z.number().int().nonnegative(), proposed: z.number().int().nonnegative() });
export const TimingPreviewResponse = z.object({
  today: IsoDate,
  people: z.number().int().nonnegative(),
  /** People past their stage's "gone quiet" days today (need a message). */
  overdue: Pair,
  /** People resting after the message limit with no reply (no message allowed yet). */
  resting: Pair,
  stages: z.array(z.object({ stage: StageCode, name: z.string(), people: z.number().int().nonnegative(), days: z.object({ current: z.number().int().nullable(), proposed: z.number().int().nullable() }), overdue: Pair })),
  /** Plain sentences, e.g. "With 7 days, 3 people would be due today (now 1)." Written in code. */
  sentences: z.array(z.string()),
});
export type TimingPreviewResponse = z.infer<typeof TimingPreviewResponse>;

// ---------------------------------------------------------------- selling rules
export const RewordRuleRequest = z.object({ title: z.string().trim().min(3).max(160), body: z.string().trim().min(10).max(3000), reason: Text(300).optional(), override: OverrideRequest.optional() }).strict();
export const AddRuleRequest = z.object({ title: z.string().trim().min(3).max(160), body: z.string().trim().min(10).max(3000), group: Text(80).optional() }).strict();
export const RuleOut = z.object({
  id: Id, code: z.string(), title: z.string(), body: z.string(), scope: z.enum(['core', 'area']), active: z.boolean(),
  reworded: z.boolean(), adjustable: z.boolean(), version: z.number().int().positive(),
});
export type RuleOut = z.infer<typeof RuleOut>;

// ---------------------------------------------------------------- history + restore
/** 'row' = a shared row changed anywhere in the app (products, facts, memberships...), versioned by a DB trigger. */
export const HistoryKind = z.enum(['profile', 'knowledge', 'prompt', 'rule', 'import', 'row', 'user', 'membership', 'model', 'setting']);
export const HistoryQuery = z.object({ kind: HistoryKind.optional(), refId: z.string().max(80).optional(), limit: z.coerce.number().int().min(1).max(200).default(50) });
export const HistoryEntry = z.object({
  id: Id, kind: HistoryKind, refId: z.string(), version: z.number().int(), action: z.string(),
  before: z.unknown().nullable(), after: z.unknown().nullable(), note: z.string().nullable(), changedByName: z.string().nullable(), changedAt: Timestamp,
  /** Why it was changed anyway (an overridden warning), when given. */
  reason: z.string().nullable().optional(),
  /** Plain-English one-liner, e.g. "Profile saved (version 4)". */
  summary: z.string(),
  canRestore: z.boolean(),
});
export type HistoryEntry = z.infer<typeof HistoryEntry>;
export const RestoreRequest = z.object({ historyId: Id, override: OverrideRequest.optional() }).strict();
export const RestoreResponse = z.object({ ok: z.literal(true), kind: HistoryKind, refId: z.string(), newVersion: z.number().int() });

// ---------------------------------------------------------------- export / import
export const AreaSetupBundle = z.object({
  format: z.literal('business-sense-area-setup'),
  formatVersion: z.literal(1),
  exportedAt: Timestamp,
  areaName: z.string(),
  profile: AreaProfileSchema,
  knowledge: z.array(z.object({ kind: KnowledgeKind, title: z.string().min(1).max(200), body: z.string().max(20000), sourceUrl: z.string().nullable(), active: z.boolean() })).max(500),
  instructions: z.array(z.object({ engineId: z.string().min(1).max(80), text: z.string().max(4000) })).max(100),
  areaRules: z.array(z.object({ code: z.string(), title: z.string().min(3).max(160), body: z.string().min(10).max(3000), active: z.boolean() })).max(200),
  rewordedRules: z.array(z.object({ code: z.string(), title: z.string().min(3).max(160), body: z.string().min(10).max(3000) })).max(200),
}).strict();
export type AreaSetupBundle = z.infer<typeof AreaSetupBundle>;
export const ImportSetupRequest = z.object({ bundle: AreaSetupBundle, profileVersion: z.number().int().nonnegative(), override: OverrideRequest.optional() }).strict();
export const ImportSetupResponse = z.object({ ok: z.literal(true), profileVersion: z.number().int(), knowledgeAdded: z.number().int(), instructionsDrafted: z.number().int(), rulesAdded: z.number().int(), rulesReworded: z.number().int(), skipped: z.array(z.string()) });

// ---------------------------------------------------------------- route table (prefix /api/v1)
type Def = { method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'; path: string; query?: z.ZodTypeAny; body?: z.ZodTypeAny; response: z.ZodTypeAny };
const d = <T extends Def>(x: T): T => x;
const Ok = z.object({ ok: z.literal(true) });

export const setupRoutes = {
  listAreas: d({ method: 'GET', path: '/setup/areas', response: z.object({ items: z.array(AreaSummary) }) }),
  renameArea: d({ method: 'PUT', path: '/setup/areas/:id/name', body: RenameAreaRequest, response: Ok }),
  getProfile: d({ method: 'GET', path: '/setup/areas/:id/profile', response: AreaProfileResponse }),
  putProfile: d({ method: 'PUT', path: '/setup/areas/:id/profile', body: PutAreaProfileRequest, response: AreaProfileResponse }),
  applyTemplate: d({ method: 'POST', path: '/setup/areas/:id/apply-template', body: ApplyTemplateRequest, response: AreaProfileResponse }),
  listKnowledge: d({ method: 'GET', path: '/setup/areas/:id/knowledge', query: KnowledgeListQuery, response: z.object({ items: z.array(KnowledgeItem) }) }),
  addKnowledge: d({ method: 'POST', path: '/setup/areas/:id/knowledge', body: CreateKnowledgeRequest, response: KnowledgeItem }),
  updateKnowledge: d({ method: 'PATCH', path: '/setup/knowledge/:id', body: UpdateKnowledgeRequest, response: KnowledgeItem }),
  removeKnowledge: d({ method: 'DELETE', path: '/setup/knowledge/:id', response: Ok }),
  presignKnowledgeUpload: d({ method: 'POST', path: '/setup/areas/:id/knowledge/presign-upload', body: KnowledgePresignUploadRequest, response: PresignUploadResponse }),
  confirmKnowledgeUpload: d({ method: 'POST', path: '/setup/areas/:id/knowledge/files/:fileId/confirm', response: KnowledgeFileOut }),
  downloadKnowledgeFile: d({ method: 'POST', path: '/setup/knowledge/:id/file/presign-download', response: PresignDownloadResponse }),
  samplePeople: d({ method: 'GET', path: '/setup/areas/:id/sample-people', query: SamplePeopleQuery, response: z.object({ items: z.array(TryPerson) }) }),
  timingPreview: d({ method: 'POST', path: '/setup/areas/:id/timing-preview', body: TimingPreviewRequest, response: TimingPreviewResponse }),
  listEngines: d({ method: 'GET', path: '/setup/areas/:id/engines', response: z.object({ items: z.array(EngineSummary) }) }),
  getEngine: d({ method: 'GET', path: '/setup/areas/:id/engines/:engineId', response: EngineDetail }),
  putInstructionDraft: d({ method: 'PUT', path: '/setup/areas/:id/engines/:engineId/draft', body: PutInstructionDraftRequest, response: EngineDetail }),
  tryEngine: d({ method: 'POST', path: '/setup/areas/:id/engines/:engineId/try', body: TryEngineRequest, response: TryEngineResponse }),
  publishInstructions: d({ method: 'POST', path: '/setup/areas/:id/engines/:engineId/publish', body: PublishInstructionRequest, response: EngineDetail }),
  restoreInstructions: d({ method: 'POST', path: '/setup/areas/:id/engines/:engineId/restore', body: RestoreInstructionRequest, response: EngineDetail }),
  listRules: d({ method: 'GET', path: '/setup/areas/:id/rules', response: z.object({ items: z.array(RuleOut) }) }),
  addRule: d({ method: 'POST', path: '/setup/areas/:id/rules', body: AddRuleRequest, response: RuleOut }),
  rewordRule: d({ method: 'PUT', path: '/setup/areas/:id/rules/:lawId', body: RewordRuleRequest, response: RuleOut }),
  resetRuleWording: d({ method: 'DELETE', path: '/setup/areas/:id/rules/:lawId/wording', response: RuleOut }),
  history: d({ method: 'GET', path: '/setup/areas/:id/history', query: HistoryQuery, response: z.object({ items: z.array(HistoryEntry) }) }),
  restore: d({ method: 'POST', path: '/setup/areas/:id/restore', body: RestoreRequest, response: RestoreResponse }),
  exportSetup: d({ method: 'GET', path: '/setup/areas/:id/export', response: AreaSetupBundle }),
  importSetup: d({ method: 'POST', path: '/setup/areas/:id/import', body: ImportSetupRequest, response: ImportSetupResponse }),
} as const;
export type SetupRouteName = keyof typeof setupRoutes;
